import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO, { SITE_URL } from '../../components/feature/SEO';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d71mgb9lum2co2q1fqg0';

const inquiryTypes = [
  '불리온 엠블럼 제작',
  '핸드메이드 자수',
  '맞춤 제작 상담',
  '대량 주문',
  '기타 문의',
];

const contactInfo = [
  {
    icon: 'ri-map-pin-line',
    title: 'Address',
    value: '서울특별시 동대문구 답십리로68길 31, 3층',
  },
  {
    icon: 'ri-phone-line',
    title: 'Phone',
    value: '010 - 8958 - 0286',
  },
  {
    icon: 'ri-mail-line',
    title: 'Email',
    value: 'info@hivantex.com',
  },
  {
    icon: 'ri-time-line',
    title: 'Business Hours',
    value: '월–금 09:00–18:00',
  },
];

const contactJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: '하이반텍스 문의 | Contact HivanTex',
    url: `${SITE_URL}/contact`,
    description: '하이반텍스에 불리온 엠블럼, 핸드메이드 자수 맞춤 제작을 문의하세요.',
    inLanguage: 'ko',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#business`,
    name: '하이반텍스 HivanTex',
    url: SITE_URL,
    telephone: '+82-10-8958-0286',
    email: 'info@hivantex.com',
    image: `${SITE_URL}/logo.png`,
    priceRange: '문의 후 안내',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '답십리로68길 31, 3층',
      addressLocality: '동대문구',
      addressRegion: '서울특별시',
      postalCode: '02579',
      addressCountry: 'KR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 37.5702,
      longitude: 127.0665,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    hasMap: 'https://maps.google.com/?q=서울특별시+동대문구+답십리로68길+31',
  },
];

type FormState = {
  name: string;
  company: string;
  email: string;
  phone: string;
  inquiryType: string;
  message: string;
};

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({
    name: '',
    company: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError('이름, 이메일, 문의 내용은 필수 입력 항목입니다.');
      return;
    }
    if (form.message.length > 500) {
      setError('문의 내용은 500자를 초과할 수 없습니다.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const body = new URLSearchParams({
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        inquiryType: form.inquiryType,
        message: form.message,
      });
      await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('제출 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen font-sans bg-cream">
      <SEO jsonLd={contactJsonLd} />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#181818] pt-40 pb-28 px-16 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 50% 60% at 20% 50%, rgba(201,169,97,0.07) 0%, transparent 60%)',
          }}
        />
        <div className="absolute top-28 left-14 w-12 h-12 border-l border-t border-gold/25" />
        <div className="absolute bottom-12 right-14 w-12 h-12 border-r border-b border-gold/25" />
        <div className="max-w-[1300px] mx-auto relative z-10">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-8 h-px bg-gold/50" />
            <span className="text-gold text-[10px] tracking-[0.3em] uppercase font-sans">Get in Touch</span>
          </div>
          <h1 className="font-serif text-cream text-6xl font-light leading-tight tracking-wide mb-6">
            Contact<br />
            <em className="not-italic text-gold">하이반텍스</em>
          </h1>
          <p className="text-cream/40 text-sm font-sans font-light leading-8 max-w-lg">
            맞춤 제작 상담, 견적 문의, 협업 제안 등 무엇이든 편하게 연락해주세요.
          </p>
        </div>
      </section>

      {/* Contact grid */}
      <section className="py-28 px-16">
        <div className="max-w-[1300px] mx-auto grid grid-cols-5 gap-16">
          {/* Left: Info */}
          <div className="col-span-2">
            <p className="text-gold text-[10px] tracking-[0.3em] uppercase font-sans mb-8">
              Contact Information
            </p>
            <div className="space-y-8 mb-16">
              {contactInfo.map((c) => (
                <div key={c.title} className="flex items-start gap-5">
                  <div className="w-9 h-9 flex items-center justify-center border border-gold/30 shrink-0 mt-0.5">
                    <i className={`${c.icon} text-gold text-sm`} />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] text-charcoal/40 uppercase font-sans mb-1">
                      {c.title}
                    </p>
                    <p className="text-charcoal text-sm font-sans font-light tracking-wide">
                      {c.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Decorative quote */}
            <div className="border-l-2 border-gold/40 pl-6">
              <p className="font-serif text-charcoal text-xl font-light leading-relaxed italic">
                "모든 상담은 비밀이 보장되며, 전문 컨설턴트가&nbsp; &nbsp; &nbsp;직접 안내해드립니다."
              </p>
              <p className="text-charcoal/40 text-xs font-sans mt-4 tracking-wide">
                — 하이반텍스 고객 서비스팀
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <div className="col-span-3">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center py-20">
                <div className="w-12 h-12 flex items-center justify-center border border-gold/40 mb-6">
                  <i className="ri-check-line text-gold text-xl" />
                </div>
                <h3 className="font-serif text-charcoal text-2xl font-light mb-4 tracking-wide">
                  문의가 접수되었습니다
                </h3>
                <p className="text-charcoal/50 text-sm font-sans font-light text-center leading-7">
                  빠른 시일 내에 담당자가 연락드리겠습니다.<br />
                  감사합니다.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="contact-main"
                className="space-y-7"
              >
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] tracking-[0.2em] text-charcoal/50 uppercase font-sans block">
                      이름 *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-charcoal/25 text-charcoal text-sm pb-2 focus:outline-none focus:border-gold transition-colors duration-200 font-sans placeholder-charcoal/25"
                      placeholder="홍길동"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] tracking-[0.2em] text-charcoal/50 uppercase font-sans block">
                      회사명
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-charcoal/25 text-charcoal text-sm pb-2 focus:outline-none focus:border-gold transition-colors duration-200 font-sans placeholder-charcoal/25"
                      placeholder="(주)회사명"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] tracking-[0.2em] text-charcoal/50 uppercase font-sans block">
                      이메일 *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-charcoal/25 text-charcoal text-sm pb-2 focus:outline-none focus:border-gold transition-colors duration-200 font-sans placeholder-charcoal/25"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] tracking-[0.2em] text-charcoal/50 uppercase font-sans block">
                      연락처
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-charcoal/25 text-charcoal text-sm pb-2 focus:outline-none focus:border-gold transition-colors duration-200 font-sans placeholder-charcoal/25"
                      placeholder="010-0000-0000"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] tracking-[0.2em] text-charcoal/50 uppercase font-sans block">
                    문의 유형
                  </label>
                  <select
                    name="inquiryType"
                    value={form.inquiryType}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-charcoal/25 text-charcoal text-sm pb-2 focus:outline-none focus:border-gold transition-colors duration-200 font-sans cursor-pointer"
                  >
                    <option value="">선택해주세요</option>
                    {inquiryTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] tracking-[0.2em] text-charcoal/50 uppercase font-sans block">
                    문의 내용 * (최대 500자)
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={5}
                    className="w-full bg-transparent border-b border-charcoal/25 text-charcoal text-sm pb-2 focus:outline-none focus:border-gold transition-colors duration-200 font-sans placeholder-charcoal/25 resize-none"
                    placeholder="문의 내용을 자세히 적어주세요."
                  />
                  <p className="text-right text-[10px] text-charcoal/30 font-sans">
                    {form.message.length}/500
                  </p>
                </div>

                {error && (
                  <p className="text-red-500 text-xs font-sans">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-charcoal text-cream text-[10px] tracking-[0.25em] uppercase py-4 hover:bg-gold hover:text-charcoal transition-all duration-300 font-sans font-medium cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  {loading ? '전송 중...' : '문의 전송하기'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
